import React, { useRef } from "react";
import IconTooltip, { IconTooltipType } from "src/components/iconTooltip";
import "./file-field.scss";

interface IFileFieldProps {
  className?: string;
  label: React.ReactNode;
  /** Currently selected file, or null when nothing is chosen yet. */
  value?: File | null;
  error?: string;
  disabled?: boolean;
  /** File-type filter for the native picker. */
  accept?: string;
  onChange: (file: File | null) => void;
}

const FileField: React.FC<IFileFieldProps> = ({
  className,
  label,
  value = null,
  error,
  disabled = false,
  accept = "image/*,application/pdf",
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldClass = `file-field ${disabled ? "file-field-disabled" : ""} ${className || ""} ${error ? "file-field-error" : ""}`;

  return (
    <div className={fieldClass}>
      <label className="file-field-label">{label}</label>

      <button
        type="button"
        className="file-field-control"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
      >
        <span className="file-field-name">{value ? value.name : "No file selected"}</span>
        <span className="file-field-browse" aria-label="Upload file">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </span>
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        disabled={disabled}
        className="file-field-input"
        onChange={(event) => onChange(event.target.files?.[0] ?? null)}
      />

      {error && (
        <IconTooltip
          iconType={IconTooltipType.INFO}
          className="icon-tooltip-negative file-field-icon-tooltip"
          tooltipId={`file-field-tooltip-${className}`}
          tooltipContent={error}
        />
      )}
    </div>
  );
};

export default FileField;
